import {
  INVOICE_TYPE,
  SuperBillColumnsType,
} from "@/pages/app/provider/billings/billing-constant";
import { getInvoiceActionOptions } from "@/pages/app/provider/billings/billing-utils";
import InvoiceStatusChip from "@/pages/app/provider/billings/common-components/InvoiceStatusChip";
import { useInvoiceRowActions } from "@/pages/app/provider/billings/hooks/use-invoice-row-actions";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  formatMoney,
  getFormattedId,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "@/utils/utils";
import { Box, Chip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { capitalize, toNumber } from "lodash";
import { useState } from "react";
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import { GlobalTableHeightWrapper } from "../../../../../../../components/GlobalTableHeightWrapper";
import Text from "../../../../../../../components/Text";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { PROVIDER_PATHS } from "../../../../../../../constants/paths";
import { useGetPatientPaymentSummary } from "../../../../../../../queries/billings/payments/use-get-patient-payment-summary";
import { useSuperBillListV2 } from "../../../../../../../queries/billings/use-superBill-list-v2";
import {
  formatDate,
  formatDateTime,
} from "../../../../../../../utils/date-time-utility";
import { FinancialOutletContext } from "./financial-filters-context";

// Amount colour for the Outstanding Balance tag, matching the Payment History
// tag it was moved from.
const AMOUNT_RED = "#F26161";

const PatientBills = () => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [_gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  // Origin so the invoice screen's Back can return to this patient-chart page
  // (instead of the Billing area). Only set when launched from the chart.
  const fromPatientChart = location.pathname + location.search;

  const { hasReadOnly } = useRoleController("Financials");

  const { invoiceSearchKey, invoiceFilter } =
    (useOutletContext<FinancialOutletContext>() ??
      {}) as Partial<FinancialOutletContext>;

  // Patient-level outstanding balance, shown as a tag above the invoice list
  // (moved here from Payment History). Clicking the tag filters the list to the
  // invoices that still carry a balance (getAllSuperBillV2 outstandingBalance);
  // clicking it again clears the filter.
  const patientUuid = useParams()?.patientId ?? "";
  const { data: paymentSummary } = useGetPatientPaymentSummary({
    patientUuid,
  });

  const [outstandingOnly, setOutstandingOnly] = useState(false);

  const toggleOutstandingFilter = () => {
    setOutstandingOnly((prev) => !prev);
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const {
    data: superBillList,
    isLoading: isSuperBillLoading,
    isFetching: isSuperBillFetching,
    refetch,
  } = useSuperBillListV2({
    pagination: pagination,
    patientId: useParams()?.patientId ?? undefined,
    xTENANTID: getXtenantId(),
    searchKey: invoiceSearchKey || undefined,
    providerId: invoiceFilter?.provider_Name?.value ?? undefined,
    billStatus:
      (invoiceFilter?.bill_Status?.value as unknown as any) ?? undefined,
    archive: invoiceFilter?.archive ?? false,
    outstandingBalance: outstandingOnly ? true : undefined,
    dateRange: invoiceFilter?.date_range ?? undefined,
    startDate:
      invoiceFilter?.date_range === "CUSTOM_RANGE"
        ? dayjs(invoiceFilter?.startDate).toISOString()
        : undefined,
    endDate:
      invoiceFilter?.date_range === "CUSTOM_RANGE"
        ? dayjs(invoiceFilter?.endDate).endOf("day").toISOString()
        : undefined,
  });

  // Row actions (menu handlers + their modals/drawers) are shared with the
  // Billing → Invoices list so both offer the same actions. `fromPath` lets the
  // invoice screens launched from here navigate back into the chart.
  const { handleInvoiceAction, invoiceActionModals, invoiceActionPermissions } =
    useInvoiceRowActions({
      onRefetch: refetch,
      fromPath: fromPatientChart,
    });

  const COLUMNS = applyDefaultColumnSettings<SuperBillColumnsType>([
    {
      field: "SuperBillID",
      flex: 1,
      minWidth: 200,
      headerName: "Invoice No & Date",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Flex gap={"10px"}>
            <Box>
              <Text
                isTabletext
                fontWeight={"medium"}
                color={"#004186"}
                sx={{ lineHeight: 1.2 }}
              >
                {`${row?.uuid ? getFormattedId(row?.uuid) : "-"}`}
              </Text>
              <Text isTabletext sx={{ color: "#1A1A1A99", lineHeight: 1.2 }}>
                {`${formatDateTime(row?.billDate ?? "")}`}
              </Text>
            </Box>
          </Flex>
        );
      },
    },
    {
      field: "encounterId",
      flex: 0.6,
      minWidth: 90,
      headerName: "Visit ID",
      sortable: false,
      renderCell: ({ row }) => (
        <Text isTabletext>
          {row?.encounterId ? getFormattedId(row?.encounterId) : "-"}
        </Text>
      ),
    },
    {
      field: "claimId",
      flex: 0.6,
      minWidth: 90,
      headerName: "Claim ID",
      sortable: false,
      renderCell: ({ row }) =>
        row?.claimId ? (
          <Text
            isTabletext
            color={"#004186"}
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() =>
              navigate(
                PROVIDER_PATHS.BILLING +
                  `claims/edit-claim?patientId=${row?.patientId}&claimId=${row?.claimId}&readOnly=true`,
                { state: { from: fromPatientChart } }
              )
            }
          >
            {getFormattedId(row?.claimId)}
          </Text>
        ) : (
          <Text isTabletext>-</Text>
        ),
    },
    {
      field: "appointmentDate",
      flex: 1,
      minWidth: 150,
      headerName: "Date of Service",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Flex gap={"10px"}>
            <Box>
              {/* <Text
                isTabletext
                fontWeight={"medium"}
                color={"#004186"}
                sx={{ lineHeight: 1.2 }}
              >
                {`${
                  isEmpty(row?.encounterId)
                    ? "-"
                    : getFormattedId(row?.encounterId)
                }`}
              </Text> */}
              <Text isTabletext sx={{ color: "#1A1A1A99", lineHeight: 1.2 }}>
                {`${
                  row?.encounterDate
                    ? formatDateTime(row?.encounterDate)
                    : row?.appointmentDate
                      ? formatDateTime(row?.appointmentDate)
                      : row?.dateOfService
                        ? formatDateTime(row?.dateOfService)
                        : ""
                }`}
              </Text>
            </Box>
          </Flex>
        );
      },
    },
    {
      field: "renderingProvider",
      flex: 0.5,
      minWidth: 150,
      headerName: "Provider",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Text isTabletext marginBottom={"1px"}>
            {row?.renderingProviderName ?? ""}
          </Text>
        );
      },
    },
    {
      field: "subTotal",
      flex: 0.5,
      minWidth: 100,
      headerName: "Invoice Amount",
      sortable: false,
      renderCell: ({ value }) => formatMoney(value),
    },
    {
      field: "prevPaid",
      flex: 0.5,
      minWidth: 100,
      headerName: "Paid",
      sortable: false,
      renderCell: ({ value }) => formatMoney(value),
    },
    {
      field: "patientBalance",
      headerName: "Patient Balance",
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: ({ value }) => formatMoney(value),
    },
    {
      field: "dueDate",
      flex: 0.5,
      minWidth: 100,
      headerName: "Due Date",
      renderCell: ({ row }) => formatDate(row?.dueDate) || "-",
    },
    {
      field: "invoiceType",
      headerName: "Invoice Type",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ value }) =>
        value === INVOICE_TYPE.PR
          ? INVOICE_TYPE.PR
          : capitalize(replaceUnderScoreAddSpace(value)) || "-",
    },
    {
      field: "superBillStatus",
      headerName: "Status",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ value }) => <InvoiceStatusChip status={value} />,
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={roleFilter({
              list: getInvoiceActionOptions(row),
              compWith: invoiceActionPermissions,
            })}
            onActionClick={(v) => handleInvoiceAction(v, row)}
          />
        );
      },
    },
  ]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {invoiceActionModals}

      {/* Hidden PDF component with prefilled data. Never triggered — nothing
          ever called pdfRef.generatePdf(), and Print Invoice goes through
          PrintSuperBill in the shared action bundle. Kept for reference.

      <PdfGenerator
        ref={pdfRef}
        onSave={(blob) => {
          setIsLoading(false);
          if (!blob) return;
          const url = window.URL.createObjectURL(blob);
          setPreviewFile(url);
          printViewDisclosure.open();
        }}
      >
        <SuperBillTemplate data={selectedSuperBillData?.data?.content?.[0]} />
      </PdfGenerator>

      <PdfViewDrawer
        w="65vw"
        file={previewFile}
        disclosureHost={printViewDisclosure}
        title="Invoice"
      /> */}

      <Flex sx={{ mb: 1, gap: 1, alignItems: "center" }}>
        <Chip
          label={
            <>
              Outstanding Balance:{" "}
              <Box component="span" sx={{ color: AMOUNT_RED, fontWeight: 600 }}>
                ${" "}
                {toNumber(paymentSummary?.data?.previousBalance ?? 0).toFixed(
                  2
                )}
              </Box>
            </>
          }
          onClick={toggleOutstandingFilter}
          variant="outlined"
          sx={{
            bgcolor: "transparent",
            cursor: "pointer",
            borderColor: outstandingOnly ? "text.primary" : "divider",
            "&:hover": { bgcolor: "transparent" },
          }}
        />
      </Flex>

      <GlobalTableHeightWrapper marginTop={0}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={superBillList?.data?.content ?? []}
          // rowHeight={60}
          columns={filterActionCol(COLUMNS, hasReadOnly || isVisitNotePage)}
          rowCount={superBillList?.data?.page?.totalElements ?? 10}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          loading={isSuperBillLoading || isSuperBillFetching}
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default PatientBills;
